import { create } from "zustand";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";
import { useClientStore } from "./useClientStore";

export interface Milestone {
  _id?: string;
  title: string;
  description?: string;
  duration?: string;
  deliverables?: { title: string; done?: boolean }[];
  amount: number;
  status: "Pending" | "In Progress" | "In Escrow" | "Released" | "Completed" | "Cancelled";
  dueDate?: string | null;
  releasedAt?: string | null;
  notes?: string;
}

export interface Project {
  _id: string;
    name: string;
  assignedContact?: {
  _id?: string;
  name?: string;
  email?: string;
  } | string;
  projectType?: string;
  designType?: string;
  website?: boolean;
  graphicDesign?: boolean;
  videoAnimation?: boolean;
  marketing?: boolean;
  trialBased?: boolean;
  supervisor?: any;
 projectStatus?: string;
  eta?: string | Date;
  projectPrice?: number | null;
  escrowAmount?: number;
  releasedPayment?: number;
  paymentStatus?: string;
  source?: "Upwork" | "Direct" | "Invitation";
  upfrontPayment?: number;
  paymentMethod?: string;
  paymentMethodNote?: string;
  createdAt?: string;
  updatedAt?: string;
  milestones?: Milestone[];
  upworkAccount?: string;
  resourceCosts?: Array<{
    _id?: string;
    label: string;
    type: "fixed" | "hourly" | "percentage";
    amount: number;
    hours?: number;
  }>;

}

export interface ProjectInput {
  name: string;
 assignedContact?: {
  _id?: string;
  name?: string;
  email?: string;
  } | string;
  projectType?: string;
  designType?: string;
  website?: boolean;
  graphicDesign?: boolean;
  videoAnimation?: boolean;
  marketing?: boolean;
  trialBased?: boolean;
  supervisor?: any;
  projectStatus?: string;
  eta?: string;
  projectPrice?: number;
  escrowAmount?: number;
  releasedPayment?: number;
  paymentStatus?: string;
  source?: "Upwork" | "Direct" | "Invitation";
  upfrontPayment?: number;
  paymentMethod?: string;
  paymentMethodNote?: string;
  sourceBid?: string;
  milestones?: Milestone[];
}

interface ProjectState {
  projects: Project[];
  selectedProject: Project | null;
  isFetchingProjects: boolean;
  isCreatingProject: boolean;
  isUpdatingProject: boolean;
  isDeletingProject: boolean;

  fetchProjects: (silent?: boolean) => Promise<void>;
  createProject: (data: ProjectInput) => Promise<any>;
  updateProject: (id: string, data: Partial<Project>) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  setSelectedProject: (project: Project | null) => void;
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  projects: [],
  selectedProject: null,
  isFetchingProjects: false,
  isCreatingProject: false,
  isUpdatingProject: false,
  isDeletingProject: false,

  fetchProjects: async (silent?: boolean) => {
    // Silent mode (background polling / live refresh) skips the loading spinner
    // and error toast so the table doesn't flicker every refresh.
    if (!silent) set({ isFetchingProjects: true });
    try {
      const res = await axiosInstance.get<Project[]>("/project");
      set({ projects: res.data });
    } catch (error: any) {
      console.error("Error fetching projects:", error);
      if (!silent) toast.error(error.response?.data?.message || "Failed to fetch projects");
    } finally {
      if (!silent) set({ isFetchingProjects: false });
    }
  },

  createProject: async (data) => {
    set({ isCreatingProject: true });
    try {
      const res: any = await axiosInstance.post<Project>("/project", data);
const clients = useClientStore.getState().clients;
       const fullAssignedContact =

     res.data.project.assignedContact
        ? clients.find(c => c._id === res.data.project.assignedContact)
        : res.data.project.assignedContact;

      const projectWithFullData = {
      ...res.data.project,
      assignedContact: fullAssignedContact,
      supervisor: { fullName: data.supervisor },
    };
     set({ projects: [projectWithFullData, ...get().projects] });
      toast.success("Project created successfully");
      // Surface the portal-provisioning result so the form can show credentials.
      return { ...projectWithFullData, portalAccount: res.data.portalAccount || null };
    } catch (error: any) {
      console.error("Error creating project:", error);
      toast.error(error.response?.data?.message || "Failed to create project");
      return null;
    } finally {
      set({ isCreatingProject: false });
    }
  },

updateProject: async (id, data) => {
  set({ isUpdatingProject: true });
  try {
    const res: any = await axiosInstance.put<Project>(`/project/${id}`, data);
const clients = useClientStore.getState().clients;
    const fullAssignedContact = res.data.project.assignedContact
      ? clients.find((c) => c._id === res.data.project.assignedContact)
      : res.data.project.assignedContact;

    const projectWithFullData = {
      ...res.data.project,
      assignedContact: fullAssignedContact,
      supervisor: { fullName: data.supervisor },
    };

    set({
      projects: get().projects.map((proj) =>
        proj._id === id ? projectWithFullData : proj
      ),
    });

    toast.success("Project updated successfully");
  } catch (error: any) {
    console.error("Error updating project:", error);
    toast.error(error.response?.data?.message || "Failed to update project");
  } finally {
    set({ isUpdatingProject: false });
  }
},

  deleteProject: async (id) => {
    set({ isDeletingProject: true });
    try {
      await axiosInstance.delete(`/project/${id}`);
      set({
        projects: get().projects.filter((p) => p._id !== id),
      });
      toast.success("Project deleted successfully");
    } catch (error: any) {
      console.error("Error deleting project:", error);
      toast.error(error.response?.data?.message || "Failed to delete project");
    } finally {
      set({ isDeletingProject: false });
    }
  },

  setSelectedProject: (project) => set({ selectedProject: project }),
}));
